import type { CustomerData } from '@/services/customer/types';

import {
  DEFAULT_ADDRESS2_FIELDS,
  DEFAULT_ADDRESS_FIELDS,
  DEFAULT_CITY_FIELDS,
  DEFAULT_COUNTRY_FIELDS,
  DEFAULT_EMAIL_FIELDS,
  DEFAULT_FIRST_NAME_FIELDS,
  DEFAULT_LAST_NAME_FIELDS,
  DEFAULT_NAME_FIELDS,
  DEFAULT_PHONE_FIELDS,
  DEFAULT_STATE_FIELDS,
  DEFAULT_ZIP_FIELDS,
} from './customer-field-defaults';
import {
  findCssFieldValue,
  findFieldValue,
  isBrowserElement,
  isHiddenField,
  isHtmlFormElement,
  mergeDefined,
  readAttribute,
  readInputValue,
  trimNonEmpty,
  uniqueNames,
} from './extractor-utils';

export type { CustomerData };

export type CustomerExtractorOptions = {
  fieldMappings?: Record<string, string[]>;
};

const EMAIL_ATTRS = ['data-email'] as const;
const PHONE_ATTRS = ['data-phone', 'data-telefone'] as const;
const NAME_ATTRS = ['data-name', 'data-nome'] as const;
const FIRST_NAME_ATTRS = ['data-first-name', 'data-firstname'] as const;
const LAST_NAME_ATTRS = ['data-last-name', 'data-lastname'] as const;
const ADDRESS_ATTRS = ['data-address'] as const;
const ADDRESS2_ATTRS = ['data-address2', 'data-apt'] as const;
const CITY_ATTRS = ['data-city'] as const;
const STATE_ATTRS = ['data-state'] as const;
const ZIP_ATTRS = ['data-zip', 'data-postal'] as const;
const COUNTRY_ATTRS = ['data-country'] as const;

/** Hidden / product fields no checkout BuyGoods (product_codename=alp1). */
const CUSTOMER_DENIED_FIELD =
  /\b(product|sku|offer|codename|account[_-]?id|item[_-]?id)\b/i;

/**
 * Extrai dados de customer a partir de Element/Form/Container.
 * Não dispara eventos, não persiste e não acessa Transport.
 *
 * Prioridade de merge: elemento alvo → form mais próximo → container.
 */
export class CustomerExtractor {
  private readonly fieldMappings: Record<string, string[]>;

  constructor(options: CustomerExtractorOptions = {}) {
    this.fieldMappings = options.fieldMappings ?? {};
  }

  extractFromElement(element: Element | null): CustomerData {
    if (!isBrowserElement(element)) return {};

    const fromSelf = this.extractFromScope(element);
    const form = element.closest('form');
    const fromForm = isHtmlFormElement(form) ? this.extractFromScope(form) : {};
    const container = this.findCustomerContainer(element);
    const fromContainer =
      container && container !== element && container !== form
        ? this.extractFromScope(container)
        : {};

    return mergeDefined<CustomerData>(fromSelf, fromForm, fromContainer);
  }

  extractFromForm(form: HTMLFormElement | null): CustomerData {
    if (!isHtmlFormElement(form)) return {};
    return this.extractFromScope(form);
  }

  extractFromContainer(container: Element | null): CustomerData {
    if (!isBrowserElement(container)) return {};
    return this.extractFromScope(container);
  }

  private extractFromScope(root: Element): CustomerData {
    const firstName = this.pick(root, {
      mappingKey: 'firstName',
      defaults: DEFAULT_FIRST_NAME_FIELDS,
      autocomplete: 'given-name',
      attrs: FIRST_NAME_ATTRS,
    });
    const lastName = this.pick(root, {
      mappingKey: 'lastName',
      defaults: DEFAULT_LAST_NAME_FIELDS,
      autocomplete: 'family-name',
      attrs: LAST_NAME_ATTRS,
    });
    const pickedName = this.pick(root, {
      mappingKey: 'name',
      defaults: DEFAULT_NAME_FIELDS,
      autocomplete: 'name',
      attrs: NAME_ATTRS,
    });
    const composedName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : (firstName ?? lastName);
    const name = firstName && lastName ? composedName : (pickedName ?? composedName);

    const rawAddress = this.pick(root, {
      mappingKey: 'address',
      defaults: DEFAULT_ADDRESS_FIELDS,
      autocomplete: ['street-address', 'address-line1'],
      attrs: ADDRESS_ATTRS,
    });
    const address =
      rawAddress && looksLikeEmail(rawAddress) ? undefined : rawAddress;
    const address2 = this.pick(root, {
      mappingKey: 'address2',
      defaults: DEFAULT_ADDRESS2_FIELDS,
      autocomplete: 'address-line2',
      attrs: ADDRESS2_ATTRS,
    });
    const postalCode = this.pick(root, {
      mappingKey: 'postalCode',
      defaults: DEFAULT_ZIP_FIELDS,
      autocomplete: 'postal-code',
      attrs: ZIP_ATTRS,
    });
    const hasBillingLocation = Boolean(address || postalCode);
    const city = this.pick(root, {
      mappingKey: 'city',
      defaults: DEFAULT_CITY_FIELDS,
      autocomplete: 'address-level2',
      attrs: CITY_ATTRS,
    });
    const state = this.pick(root, {
      mappingKey: 'state',
      defaults: DEFAULT_STATE_FIELDS,
      autocomplete: 'address-level1',
      attrs: STATE_ATTRS,
    });
    const country = this.pick(root, {
      mappingKey: 'country',
      defaults: DEFAULT_COUNTRY_FIELDS,
      autocomplete: ['country-name', 'country'],
      attrs: COUNTRY_ATTRS,
    });

    return mergeDefined<CustomerData>({
      email: this.pick(root, {
        mappingKey: 'email',
        defaults: DEFAULT_EMAIL_FIELDS,
        type: 'email',
        autocomplete: 'email',
        attrs: EMAIL_ATTRS,
      }),
      phone: this.pick(root, {
        mappingKey: 'phone',
        defaults: DEFAULT_PHONE_FIELDS,
        type: 'tel',
        autocomplete: 'tel',
        attrs: PHONE_ATTRS,
      }),
      name: trimNonEmpty(name),
      firstName,
      lastName,
      address,
      address2,
      city: hasBillingLocation ? city : undefined,
      state: hasBillingLocation ? state : undefined,
      postalCode,
      country: hasBillingLocation ? country : undefined,
    });
  }

  private pick(
    root: Element,
    options: {
      mappingKey: string;
      defaults: readonly string[];
      type?: string;
      autocomplete?: string | readonly string[];
      attrs?: readonly string[];
    }
  ): string | undefined {
    const typed = options.type
      ? this.readTypedInput(root, options.type)
      : undefined;
    const autocompletes = options.autocomplete
      ? Array.isArray(options.autocomplete)
        ? options.autocomplete
        : [options.autocomplete]
      : [];

    let auto: string | undefined;
    for (const token of autocompletes) {
      auto = this.readAutocomplete(root, token);
      if (auto) break;
    }

    const candidates = this.fieldsFor(options.mappingKey, options.defaults);

    return (
      typed ??
      auto ??
      (options.attrs ? readAttribute(root, options.attrs) : undefined) ??
      findCssFieldValue(root, candidates) ??
      findFieldValue(root, candidates, {
        ignoreHidden: true,
        denyNamePattern: CUSTOMER_DENIED_FIELD,
      })
    );
  }

  private fieldsFor(key: string, defaults: readonly string[]): string[] {
    return uniqueNames(this.fieldMappings[key], defaults);
  }

  private readTypedInput(root: Element, type: string): string | undefined {
    const selector = `input[type="${type}"]`;
    const input = root.matches(selector) ? root : root.querySelector(selector);
    if (!input || isHiddenField(input)) return undefined;
    return readInputValue(input);
  }

  private readAutocomplete(
    root: Element,
    autocomplete: string
  ): string | undefined {
    const needle = autocomplete.toLowerCase();
    const nodes: Element[] = [];
    if (
      root.matches('input, select, textarea') &&
      autocompleteTokens(root).includes(needle)
    ) {
      nodes.push(root);
    }
    nodes.push(
      ...Array.from(root.querySelectorAll('input, select, textarea')).filter(
        el => autocompleteTokens(el).includes(needle)
      )
    );
    for (const input of nodes) {
      if (isHiddenField(input)) continue;
      const value = readInputValue(input);
      if (value) return value;
    }
    return undefined;
  }

  private findCustomerContainer(element: Element): Element | null {
    return element.closest(
      '[data-email], [data-phone], [data-name], [data-track], .lead-form, .checkout-form, #checkout-form, #checkout'
    );
  }
}

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function autocompleteTokens(el: Element): string[] {
  const raw = (el.getAttribute('autocomplete') ?? '').trim().toLowerCase();
  if (!raw) return [];
  return raw.split(/\s+/).filter(Boolean);
}
